import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Printer,
  Edit3,
  ArrowLeft,
  Loader2,
  FileText,
  IndianRupee,
  CheckCircle2,
  AlertCircle,
  Building,
  Activity,
  Calendar,
  CreditCard,
  User,
  Check
} from 'lucide-react';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import styles from './ViewPaymentReceived.module.css';
import api from '../../api/axios';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import toast from 'react-hot-toast';
import PaymentReceivedDrawer from './PaymentReceivedDrawer';
import NotFound from '../Error/NotFound';


const ViewPaymentReceived = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [payment, setPayment] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const fetchPayment = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/customer-payments/${id}`);
      if (response.data.success) {
        setPayment(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching payment details:', error);
      toast.error('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayment();
  }, [id]);

  const getPaymentModeLabel = (mode) => {
    const modes = {
      1: 'Cash',
      2: 'Bank Transfer',
      3: 'Cheque',
      4: 'Online'
    };
    return modes[mode] || 'Other';
  };

  const getModeClass = (mode) => {
    const classes = {
      1: 'cash',
      2: 'bank',
      3: 'cheque',
      4: 'online'
    };
    return classes[mode] || '';
  };

  if (loading) {
    return (
      <div className={styles['view-payment-container']}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <div className={styles['content-wrapper']}>
          <Header onMenuClick={() => setIsSidebarOpen(true)} />
          <div className={styles['loading-container']}>
            <Loader2 size={40} className={styles['spinner']} />
            <p>Gathering Details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return <NotFound />;
  }


  const invoice = payment.sales_invoice || {};
  const totalReceived = parseFloat(invoice.payments_received_sum_amount || 0);
  const invoiceTotal = parseFloat(invoice.total_amount || 0);
  const balance = invoiceTotal - totalReceived;

  return (
    <div className={`${styles['view-payment-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={() => setIsSidebarOpen(true)} />

        <main className={styles['main-content']}>
          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <Breadcrumbs />
              <h1>{payment.ref_transaction_number || `REC-${payment.id}`}</h1>
              <p>Work Order Payment details and verification status.</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['btn-secondary']} onClick={() => navigate('/payment/payments-received')}>
                <ArrowLeft size={18} />
                <span>Back</span>
              </button>
              <button className={styles['btn-secondary']} onClick={() => window.print()}>
                <Printer size={18} />
                <span>Print Receipt</span>
              </button>
              <button className={styles['btn-primary']} onClick={() => setIsDrawerOpen(true)}>
                <Edit3 size={18} />
                <span>Edit Record</span>
              </button>
            </div>
          </div>

          <div className={styles['payment-dashboard-header']}>
            <div className={styles['header-top']}>
              <div className={styles['title-flex']}>
                <h1>{payment.ref_transaction_number || `REC-${payment.id}`}</h1>
                <span className={`${styles['status-badge']} ${styles[getModeClass(payment.payment_mode)] || ''}`}>
                  <span className={styles['dot']}></span>
                  {getPaymentModeLabel(payment.payment_mode).toUpperCase()}
                </span>
              </div>
              
              <div className={styles['payment-meta']}>
                <div className={styles['meta-item']}>
                  <Building size={16} />
                  <span>{payment.project?.project_name}</span>
                </div>
                <div className={styles['meta-item']}>
                  <User size={16} />
                  <span>{payment.project?.customer?.customer_name}</span>
                </div>
                <div className={styles['meta-item']}>
                  <Calendar size={16} />
                  <span>{new Date(payment.payment_date).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className={styles['metrics-row']}>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-txn']}`}><CheckCircle2 size={20} /></div>
                <span className={styles['m-trend']}>{new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
              </div>
              <div className={styles['m-info']}>
                <label>Amount Received</label>
                <h2 style={{ color: '#10b981' }}>₹{parseFloat(payment.amount).toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-total']}`}><IndianRupee size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Invoice Total</label>
                <h2>₹{invoiceTotal.toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-received']}`}><CheckCircle2 size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Total Received to Date</label>
                <h2 style={{ color: '#10b981' }}>₹{totalReceived.toLocaleString('en-IN')}</h2>
              </div>
            </div>
            <div className={styles['metric-card']}>
              <div className={styles['m-header']}>
                <div className={`${styles['m-icon']} ${styles['bg-balance']}`}><AlertCircle size={20} /></div>
              </div>
              <div className={styles['m-info']}>
                <label>Outstanding Balance</label>
                <h2 style={{ color: '#f97316' }}>₹{balance.toLocaleString('en-IN')}</h2>
              </div>
            </div>
          </div>

          <div className={styles['dashboard-grid']}>
            <div className={styles['left-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><CreditCard size={20} /><h3>Transaction Details</h3></div>
                </div>
                <div className={styles['details-list']}>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Payment Date</span>
                    <span className={styles['d-value']}>{new Date(payment.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Payment Mode</span>
                    <span className={styles['d-value']}>{getPaymentModeLabel(payment.payment_mode)}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Reference / Transaction #</span>
                    <span className={styles['d-value']}>{payment.ref_transaction_number || 'N/A'}</span>
                  </div>
                  <div className={styles['detail-item']}>
                    <span className={styles['d-label']}>Deposited In (Bank Account)</span>
                    <span className={styles['d-value']}>{payment.bank_account?.bank_name || 'N/A'}</span>
                  </div>
                  {payment.tds_amount > 0 && (
                    <div className={styles['detail-item']}>
                      <span className={styles['d-label']}>TDS Deducted</span>
                      <span className={styles['d-value']} style={{ color: '#ef4444' }}>₹{parseFloat(payment.tds_amount).toLocaleString('en-IN')}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><FileText size={20} /><h3>Related Sales Invoice</h3></div>
                </div>
                <div className={styles['invoice-preview-box']}>
                  <div className={styles['invoice-preview-header']}>
                    <div className={styles['inv-tag']}>{invoice.invoice_number || 'N/A'}</div>
                    <span className={styles['d-label']}>Invoice Date: {new Date(invoice.invoice_date).toLocaleDateString()}</span>
                  </div>
                  <div className={styles['inv-details']}>
                    <div className={styles['inv-stat']}>
                      <label>Invoice Value</label>
                      <span>₹{invoiceTotal.toLocaleString('en-IN')}</span>
                    </div>
                    <div className={styles['inv-stat']}>
                      <label>Due Date</label>
                      <span>{invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><FileText size={18} /><h3>Remarks / Internal Notes</h3></div>
                </div>
                <p className={styles['note-text']}>{payment.remarks || 'No specific remarks provided for this receipt.'}</p>
              </div>
            </div>

            <div className={styles['right-column']}>
              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><Building size={20} /><h3>Stakeholders</h3></div>
                </div>
                <div className={styles['stakeholder-list']}>
                  <div className={styles['st-item']}>
                    <div className={styles['st-avatar']} style={{ background: '#fef2f2', color: '#dc2626' }}>C</div>
                    <div className={styles['st-info']}>
                      <label>CUSTOMER</label>
                      <h4>{payment.project?.customer?.customer_name || 'N/A'}</h4>
                    </div>
                  </div>
                  <div className={styles['st-item']}>
                    <div className={styles['st-avatar']} style={{ background: '#ffedd5', color: '#f97316' }}>P</div>
                    <div className={styles['st-info']}>
                      <label>PROJECT</label>
                      <h4>{payment.project?.project_name}</h4>
                    </div>
                  </div>
                  <div className={styles['st-item']}>
                    <div className={styles['st-avatar']} style={{ background: '#f1f5f9', color: '#475569' }}>U</div>
                    <div className={styles['st-info']}>
                      <label>ENTERED BY</label>
                      <h4>{payment.creator?.name || 'System'}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><Activity size={20} /><h3>Financial Overview</h3></div>
                </div>
                <div className={styles['info-table']}>
                  <div className={styles['s-row']}><span>Invoice Amount</span><span>₹{invoiceTotal.toLocaleString()}</span></div>
                  <div className={styles['s-row']}><span>Received Before</span><span>₹{(totalReceived - parseFloat(payment.amount)).toLocaleString()}</span></div>
                  <div className={styles['s-row']}><span>Current Receipt</span><span>₹{parseFloat(payment.amount).toLocaleString()}</span></div>
                  {payment.tds_amount > 0 && (
                    <div className={styles['s-row']} style={{ color: '#ef4444' }}><span>TDS Deducted</span><span>+ ₹{parseFloat(payment.tds_amount).toLocaleString()}</span></div>
                  )}
                  <div className={styles['s-divider']}></div>
                  <div className={`${styles['s-row']} ${styles['highlight']}`}><span>Net Received</span><span>₹{totalReceived.toLocaleString()}</span></div>
                  <div className={styles['s-divider']}></div>
                  <div className={`${styles['s-row']} ${styles['outstanding']}`}><span>Outstanding Balance</span><span>₹{balance.toLocaleString()}</span></div>
                </div>
              </div>

              <div className={styles['standard-card']}>
                <div className={styles['card-header']}>
                  <div className={styles['h-title']}><Activity size={18} /><h3>Activity Status</h3></div>
                </div>
                <div className={styles['timeline-v']}>
                  <div className={`${styles['tl-item']} ${styles['active']}`}>
                    <div className={styles['tl-dot']}><Check size={10} /></div>
                    <div className={styles['tl-content']}>
                      <h4>Payment Received</h4>
                      <p>{new Date(payment.payment_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <div className={`${styles['tl-item']} ${styles['active']}`}>
                    <div className={styles['tl-dot']}><Check size={10} /></div>
                    <div className={styles['tl-content']}>
                      <h4>Verified & Recorded</h4>
                      <p>{new Date(payment.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </main>
      </div>

      <PaymentReceivedDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSave={fetchPayment}
        paymentId={payment.id}
      />
    </div>
  );
};

export default ViewPaymentReceived;
